import { ReactNode } from 'react';

import { useRouteRef } from '@backstage/core-plugin-api';

import {
  IconLayout,
  ModuleIcons,
  extractLastNameComponent,
  extractWorkflowFromInstanceName,
  workflowToIcon,
} from '@spotify/confidence-core-react';
import {
  AppIdentityFragment,
  getTypeOrNull,
  useConfidenceSearchQuery,
} from '@spotify/confidence-graphql';
import {
  assignmentTableRouteRef,
  factTableRouteRef,
} from '@spotify/plugin-confidence-admin';
import { eventRouteRef } from '@spotify/plugin-confidence-events';
import { flagRouteRef } from '@spotify/plugin-confidence-flags';
import { metricRouteRef } from '@spotify/plugin-confidence-metrics';
import {
  useFormatWorkflowDisplayName,
  workflowInstanceRouteRef,
} from '@spotify/plugin-confidence-workflows-react';

import { ModuleLink } from '../components';
import { commandScore } from '../utils/score';

export const isModuleLink = (
  value?: ModuleLink | ReactNode,
): value is ModuleLink => (value ? value.hasOwnProperty('to') : false);

export interface SearchDocument {
  id: string;
  title: string;
  subtitle: string;
  category: string;
  href: string;
  icon: ReactNode;
}

const owner = (identity: AppIdentityFragment | undefined | null) => {
  if (identity?.user) {
    return getTypeOrNull(identity.user)?.fullName ?? 'unknown';
  }

  if (identity?.group) {
    return getTypeOrNull(identity.group)?.displayName ?? 'unknown';
  }

  return 'unknown';
};

export const useSearch = (
  query: string,
  menuLinks: (ModuleLink | ReactNode)[],
) => {
  const results: SearchDocument[] = [];

  const formatWorkflowDisplayName = useFormatWorkflowDisplayName();

  const workflowInstanceRoute = useRouteRef(workflowInstanceRouteRef);
  const assignmentTableRoute = useRouteRef(assignmentTableRouteRef);
  const metricRoute = useRouteRef(metricRouteRef);
  const factTableRoute = useRouteRef(factTableRouteRef);
  const flagRoute = useRouteRef(flagRouteRef);
  const eventRoute = useRouteRef(eventRouteRef);

  const { data, loading } = useConfidenceSearchQuery({
    variables: {
      filter: query && `displayName:${query}*`,
      flagsFilter: query && `name:*${query}*`,
      eventsFilter: query && `name:*${query}*`,
      pageSize: 10,
    },
    skip: query.length === 0,
  });

  const workflowInstances = getTypeOrNull(
    data?.workflowInstances,
  )?.workflowInstances;

  if (workflowInstances?.length) {
    results.push(
      ...workflowInstances.map(i => {
        const workflow = extractWorkflowFromInstanceName(i.name)!;
        const category = formatWorkflowDisplayName(workflow, {
          capitalize: true,
        });
        const Icon = workflowToIcon(workflow);
        return {
          id: i.name,
          title: i.displayName,
          subtitle: owner(getTypeOrNull(i.owner)),
          category,
          href: workflowInstanceRoute({
            workflow: extractWorkflowFromInstanceName(i.name)!,
            id: extractLastNameComponent(i.name)!,
          }),
          icon: <Icon />,
        };
      }),
    );
  }

  const metrics = getTypeOrNull(data?.metrics)?.metrics;

  if (metrics?.length) {
    results.push(
      ...metrics.map(m => ({
        id: m.name,
        title: m.displayName,
        subtitle: owner(getTypeOrNull(m.owner)),
        category: 'Metric',
        href:
          metricRoute?.({
            id: extractLastNameComponent(m.name)!,
          }) ?? '',
        icon: <ModuleIcons.metrics />,
      })),
    );
  }

  const factTables = getTypeOrNull(data?.factTables)?.factTables;

  if (factTables?.length) {
    results.push(
      ...factTables.map(f => ({
        id: f.name,
        title: f.displayName,
        subtitle: owner(getTypeOrNull(f.owner)),
        category: 'Fact table',
        href:
          factTableRoute?.({
            id: extractLastNameComponent(f.name)!,
          }) ?? '',
        icon: <ModuleIcons.metrics />,
      })),
    );
  }

  const flags = getTypeOrNull(data?.flags)?.flags;

  if (flags?.length) {
    results.push(
      ...flags.map(f => ({
        id: f.name,
        title: extractLastNameComponent(f.name)!,
        subtitle: owner(getTypeOrNull(f.owner)),
        category: 'Flag',
        href:
          flagRoute?.({
            id: extractLastNameComponent(f.name)!,
          }) ?? '',
        icon: <ModuleIcons.flags />,
      })),
    );
  }

  const assignmentTables = getTypeOrNull(
    data?.assignmentTables,
  )?.assignmentTables;

  if (assignmentTables?.length) {
    results.push(
      ...assignmentTables.map(a => ({
        id: a.name,
        title: a.displayName,
        category: 'Assignment table',
        subtitle: owner(getTypeOrNull(a.owner)),
        href:
          assignmentTableRoute?.({
            id: extractLastNameComponent(a.name)!,
          }) ?? '',
        icon: <ModuleIcons.metrics />,
      })),
    );
  }

  const eventDefinitions = getTypeOrNull(
    data?.eventDefinitions,
  )?.eventDefinitions;

  if (eventDefinitions?.length) {
    results.push(
      ...eventDefinitions.map(e => ({
        id: e.name,
        title: extractLastNameComponent(e.name)!,
        subtitle: owner(getTypeOrNull(e.owner)),
        category: 'Event',
        href:
          eventRoute({
            id: extractLastNameComponent(e.name)!,
          }) ?? '',
        icon: <ModuleIcons.events />,
      })),
    );
  }

  const pagesLinks = menuLinks
    .filter(m => isModuleLink(m))
    .flatMap(m => {
      // just to make ts happy
      if (isModuleLink(m)) {
        return m?.children
          ? m.children.map((child: ModuleLink) => ({
              url: child.to,
              title: child.label,
              icon: child.icon,
            }))
          : [{ url: m.to, title: m.label, icon: m.icon }];
      }
      return [];
    })
    .filter(p => query && p.title.toLowerCase().includes(query.toLowerCase()));

  if (pagesLinks.length) {
    results.push(
      ...pagesLinks.map(p => {
        const Icon = p.icon ?? IconLayout;

        return {
          id: p.url,
          title: p.title,
          subtitle: 'Page',
          category: 'Page',
          href: p.url,
          icon: <Icon />,
        };
      }),
    );
  }

  const scores = Object.fromEntries(
    results.map(r => [r.id, commandScore(r.title, query, [])]),
  );

  const ranked = results.sort((a, b) => scores[b.id] - scores[a.id]);

  return { results: ranked, loading };
};
